import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Building2, ShieldCheck, BadgeCheck, Clock, Package, ChevronDown } from "lucide-react";
import Seo, { faqSchema, WEBSITE_SCHEMA } from "@/components/Seo";
import { Reveal, SectionHeading, CallButton, RequestButton, TrustChips, TrustStrip, GoogleReviewsBlock, CtaBand } from "@/components/Blocks";
import { BUSINESS, BRANDS, STEPS, HOME_FAQS, PAYMENT_METHODS, PARTS_NOTE } from "@/data/site";
import { SERVICES } from "@/data/services";
import { COUNTIES, CITIES } from "@/data/locations";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG = "https://images.pexels.com/photos/38190070/pexels-photo-38190070.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PRICING_IMG = "https://images.unsplash.com/photo-1556911220-bff31c812dba?auto=format&fit=crop&w=1200&q=70";

const lineVariant = {
    hidden: { y: "115%" },
    visible: (i) => ({
        y: 0,
        transition: { delay: 0.15 + i * 0.13, duration: 0.75, ease: [0.22, 1, 0.36, 1] },
    }),
};

function HeroLine({ children, index, className = "" }) {
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span className={`block ${className}`} custom={index} variants={lineVariant} initial="hidden" animate="visible">
                {children}
            </motion.span>
        </span>
    );
}

const PROBLEM_CHIPS = [
    ["Refrigerator not cooling", "/refrigerator-repair"], ["Refrigerator leaking", "/refrigerator-repair"],
    ["Ice maker not working", "/ice-maker-repair"], ["Freezer frosting up", "/freezer-repair"],
    ["Washer not draining", "/washer-repair"], ["Washer not spinning", "/washer-repair"],
    ["Washer shaking violently", "/washer-repair"], ["Dryer not heating", "/dryer-repair"],
    ["Dryer taking forever", "/dryer-repair"], ["Dishwasher not draining", "/dishwasher-repair"],
    ["Dishwasher not cleaning", "/dishwasher-repair"], ["Oven not heating", "/oven-repair"],
    ["Oven temperature off", "/oven-repair"], ["Burner won't ignite", "/range-stove-repair"],
];

export default function HomePage() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);
    const [showAllProblems, setShowAllProblems] = useState(false);
    const visibleChips = showAllProblems ? PROBLEM_CHIPS : PROBLEM_CHIPS.slice(0, 8);

    return (
        <div>
            <Seo
                title="Appliance Repair Brevard & Seminole County FL | Appliance Mania"
                description="In-home appliance repair in Brevard & Seminole County, FL. $155 service call waived with completed repair. 6-month warranty. Call (321) 475-2230."
                path="/"
                schemas={[WEBSITE_SCHEMA, faqSchema(HOME_FAQS.slice(0, 4))]}
            />

            {/* HERO */}
            <section ref={heroRef} className="relative overflow-hidden bg-[#FAFAFA]" data-testid="hero-section">
                <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-amber-200/40 blur-3xl" />
                <div className="pointer-events-none absolute top-40 -left-24 h-72 w-72 rounded-full bg-red-100/50 blur-3xl" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8 sm:pt-14 sm:pb-16 grid lg:grid-cols-2 gap-10 items-center relative">
                    <div>
                        <motion.p
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                            className="font-mono-brand text-xs font-bold uppercase tracking-[0.25em] text-red-600 mb-4"
                            data-testid="hero-kicker">
                            Brevard County · Seminole County · Florida
                        </motion.p>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-900">
                            <HeroLine index={0}>Professional</HeroLine>
                            <HeroLine index={1} className="text-red-600">Appliance Repair,</HeroLine>
                            <HeroLine index={2}>Done <span className="gold-underline">Right at Home.</span></HeroLine>
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
                            className="mt-4 sm:mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                            Fast, professional in-home repair for refrigerators, washers, dryers, dishwashers, ovens, and ranges —
                            gas and electric — serving homeowners across Brevard &amp; Seminole Counties.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.6 }}
                            className="mt-5 sm:mt-6 flex flex-col sm:flex-row gap-3">
                            <CallButton location="hero" testid="hero-call-btn" />
                            <RequestButton testid="hero-request-btn">Request Service Online</RequestButton>
                        </motion.div>
                        <motion.p
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
                            className="mt-3 text-sm text-slate-500">
                            <strong className="text-slate-700">$155 service call — waived with completed repair.</strong> Most appointments within about 24 hours, subject to availability.
                        </motion.p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
                        className="relative hidden lg:block">
                        <motion.div style={{ y: imgY }} className="relative">
                            <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white rotate-1">
                                <img src={HERO_IMG} alt="Technician performing in-home appliance repair" className="w-full h-[400px] object-cover" width="940" height="400" fetchPriority="high" />
                            </div>
                            <div className="absolute -bottom-6 -left-8 rounded-2xl bg-slate-950 text-white px-6 py-4 shadow-2xl -rotate-1">
                                <p className="font-mono-brand text-[10px] uppercase tracking-widest text-amber-400">Every completed repair</p>
                                <p className="font-heading text-xl font-extrabold mt-1">6-Month Warranty</p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <TrustStrip />

            {/* TRUST CHIPS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
                <TrustChips />
            </section>

            {/* SERVICES */}
            <section id="services" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14" data-testid="services-section">
                <SectionHeading
                    kicker="What we fix"
                    title="In-home repair for every major appliance"
                    sub="One local company for your whole kitchen and laundry room — gas and electric. Clear diagnosis, upfront quotes, and a 6-month warranty on completed repairs."
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
                    {SERVICES.map((s, i) => (
                        <Reveal key={s.slug} delay={(i % 4) * 0.05}>
                            <Link to={`/${s.slug}`} className="group block h-full rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                                data-testid={`service-card-${s.slug}`}>
                                <div className="h-32 sm:h-32 overflow-hidden">
                                    <img src={s.image} alt={`${s.name} in Brevard and Seminole County, FL`} loading="lazy" width="600" height="288"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                </div>
                                <div className="p-4">
                                    <div className="flex items-center gap-2">
                                        <s.icon className="h-5 w-5 text-red-600 shrink-0" />
                                        <h3 className="font-heading text-lg font-bold text-slate-900 leading-tight">{s.name}</h3>
                                    </div>
                                    <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{s.cardDesc}</p>
                                    <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-red-600 group-hover:gap-2 transition-all">
                                        Learn more <ArrowRight className="h-3.5 w-3.5" />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* COMMON PROBLEMS */}
            <section className="bg-slate-100/70 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
                    <SectionHeading
                        kicker="Sound familiar?"
                        title="Tell us what your appliance is doing"
                        sub="These are problems we repair in homes across both counties. If yours is on this list, it's often fixable."
                    />
                    <div className="flex flex-wrap gap-2.5" data-testid="problem-chips">
                        {visibleChips.map(([label, to]) => (
                            <Link key={label} to={to}
                                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-red-500 hover:text-red-600 hover:shadow-sm transition-all"
                                data-testid={`problem-chip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                {label}
                            </Link>
                        ))}
                    </div>
                    <button type="button" onClick={() => setShowAllProblems(!showAllProblems)}
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700 transition-colors"
                        data-testid="problems-toggle-btn" aria-expanded={showAllProblems}>
                        {showAllProblems ? "Show fewer problems" : "See more common problems"}
                        <ChevronDown className={`h-4 w-4 transition-transform ${showAllProblems ? "rotate-180" : ""}`} />
                    </button>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14" data-testid="how-it-works">
                <SectionHeading kicker="How service works" title="Four steps. No surprises." />
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-5">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.num} delay={i * 0.07}>
                            <div className="relative rounded-2xl border border-slate-200 bg-white p-5 h-full shadow-sm">
                                <span className="font-heading text-4xl font-extrabold text-amber-400/90">{s.num}</span>
                                <h3 className="mt-2.5 font-heading text-lg font-bold text-slate-900">{s.title}</h3>
                                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* PRICING + WHY */}
            <section className="bg-slate-950 text-white dark-grain">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid lg:grid-cols-2 gap-10 items-center">
                    <Reveal>
                        <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-3">Honest pricing</p>
                        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                            $155 Service Call — <span className="text-amber-400">Waived With Completed Repair</span>
                        </h2>
                        <p className="mt-4 text-slate-200 leading-relaxed">
                            Here's exactly how it works: we come to your home, diagnose the appliance, and give you a clear
                            repair quote. You decide whether to proceed. If you approve the repair and we complete it,
                            <strong className="text-white"> the $155 service-call fee is waived</strong> — you only pay for the repair itself.
                        </p>
                        <div className="mt-5 space-y-3">
                            {[
                                [ShieldCheck, "6-month warranty on completed repairs"],
                                [Clock, "Most appointments within about 24 hours, subject to availability"],
                                [Package, PARTS_NOTE],
                                [BadgeCheck, "After-hours emergency service may be available 10 PM – 6 AM — call for availability"],
                            ].map(([Icon, text]) => (
                                <div key={text} className="flex items-start gap-3 text-sm text-slate-100">
                                    <Icon className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" /> {text}
                                </div>
                            ))}
                        </div>
                        <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row gap-3">
                            <CallButton location="pricing-section" testid="pricing-call-btn" />
                            <RequestButton testid="pricing-request-btn" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="rounded-3xl overflow-hidden shadow-2xl border border-slate-800 -rotate-1">
                            <img src={PRICING_IMG}
                                alt="Modern home kitchen with built-in oven and appliances" className="w-full h-[340px] object-cover" width="1200" height="340" loading="lazy" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* BRANDS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14" data-testid="brands-section">
                <SectionHeading
                    kicker="Brands we service"
                    title="All the major brands in your kitchen and laundry room"
                    sub="We service most major residential appliance brands found in homes across Brevard and Seminole Counties."
                />
                <div className="flex flex-wrap gap-2.5">
                    {BRANDS.map((b) => (
                        <span key={b} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 font-heading font-bold text-slate-700 shadow-sm">{b}</span>
                    ))}
                    <span className="rounded-xl border border-dashed border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-500 self-center">…and other major residential brands</span>
                </div>
                <p className="mt-4 text-xs text-slate-500">
                    Appliance Mania is an independent repair company and is not affiliated with or authorized by these manufacturers.
                    <Link to="/brands-we-service" className="ml-1 text-red-600 font-semibold hover:underline">See all brands</Link>
                </p>
            </section>

            {/* SERVICE AREAS */}
            <section id="areas" className="bg-slate-100/70 border-y border-slate-200" data-testid="areas-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
                    <SectionHeading
                        kicker="Where we work"
                        title="Serving Brevard & Seminole Counties"
                        sub="Real in-home service across the Space Coast and Seminole County — not a call center, not a referral network."
                    />
                    <div className="grid gap-5 md:grid-cols-2">
                        {COUNTIES.map((c, i) => (
                            <Reveal key={c.slug} delay={i * 0.08}>
                                <Link to={`/${c.slug}`} className="group block h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                                    data-testid={`county-card-${c.slug}`}>
                                    <h3 className="font-heading text-xl font-bold text-slate-900 group-hover:text-red-600 transition-colors">{c.name}, Florida</h3>
                                    <p className="mt-2.5 text-sm text-slate-600 leading-relaxed line-clamp-2">{c.intro[0]}</p>
                                    <div className="mt-3.5 flex flex-wrap gap-1.5">
                                        {c.cities.slice(0, 8).map((city) => (
                                            <span key={city.name} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{city.name}</span>
                                        ))}
                                        {c.cities.length > 8 && <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">+ more</span>}
                                    </div>
                                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-red-600 group-hover:gap-2 transition-all">
                                        Appliance repair in {c.name} <ArrowRight className="h-4 w-4" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.1}>
                        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm" data-testid="popular-areas">
                            <h3 className="font-heading text-lg font-bold text-slate-900 mb-4">Popular Service Areas</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                {CITIES.map((c) => (
                                    <Link key={c.slug} to={`/${c.slug}`}
                                        className="group flex items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:border-red-400 hover:text-red-600 hover:bg-white transition-all"
                                        data-testid={`popular-area-${c.slug}`}>
                                        <ArrowRight className="h-3.5 w-3.5 text-red-500 shrink-0" />
                                        Appliance Repair {c.name}, FL
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* GOOGLE REVIEWS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14" data-testid="reviews-section">
                <Reveal>
                    <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
                        <GoogleReviewsBlock />
                    </div>
                </Reveal>
            </section>

            {/* PROPERTY MANAGERS BANNER */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-14">
                <Reveal>
                    <div className="rounded-3xl bg-slate-950 dark-grain p-6 sm:p-10 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
                        <div>
                            <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-3">Property managers &amp; landlords</p>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
                                Priority appliance repair for your rentals
                            </h2>
                            <p className="mt-3 text-slate-200 max-w-xl leading-relaxed">
                                $115 service calls, discounted services and repairs, and priority scheduling for property managers,
                                landlords, and rental-property owners across Brevard &amp; Seminole Counties.
                            </p>
                        </div>
                        <Link to="/property-managers"
                            className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-6 py-4 rounded-xl shadow-lg transition-all hover:-translate-y-0.5 whitespace-nowrap"
                            data-testid="pm-banner-btn">
                            <Building2 className="h-5 w-5" /> Get Priority Service
                        </Link>
                    </div>
                </Reveal>
            </section>

            {/* FAQ */}
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14" data-testid="faq-section">
                <SectionHeading center kicker="Questions" title="Frequently asked questions" />
                <Reveal>
                    <Accordion type="single" collapsible className="w-full">
                        {HOME_FAQS.map((f, i) => (
                            <AccordionItem key={i} value={`faq-${i}`}>
                                <AccordionTrigger className="text-left font-heading font-semibold text-slate-900" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>
                <p className="mt-5 text-center text-sm text-slate-500">
                    We accept {PAYMENT_METHODS.join(", ")}. <Link to="/faq" className="text-red-600 font-semibold hover:underline">More questions</Link>
                </p>
            </section>

            <CtaBand
                title={<>Appliance acting up? Call <span className="text-amber-400">Appliance Mania.</span></>}
                sub="Call anytime. Tell us what your appliance is doing — we'll tell you honestly whether we can help, and most appointments can be scheduled within about 24 hours, subject to availability."
                testid="final-cta"
            />
        </div>
    );
}
